import React, { useState, useMemo, useCallback } from 'react';
import { C } from './analytics/AnalyticsTokens';
import { STATUS_CONFIG, STATUS_OPTIONEN } from './analytics/AnalyticsConstants';
import { computeWeeklyBuckets, plattformAusStelle, formatDate, trendVsPrev } from './analytics/AnalyticsUtils';
import AnalyticsCalendar from './analytics/AnalyticsCalendar';
import { ExternModal } from './analytics/AnalyticsComponents';
import { StelleCard } from './analytics/StelleCard';
import { useAnalyticsTodos, INITIAL_TODOS } from './analytics/useAnalyticsTodos';
import MobileBottomSheet from './components/MobileBottomSheet';
import { updateStelle, createStelle } from './api';
import { istEntwurf } from './utils/themeUtils';
import './Analytics.css';

// ── Design tokens (mirrors design-bundle cvflow-tokens) ───────────────────
const BG       = '#F5F2E9';
const SURFACE  = '#FEFCF6';
const CREAM2   = '#FDFAF4';
const INK      = '#0F1E1A';
const SOFT     = '#5A5550';
const MUTED    = '#888880';
const FAINT    = '#B8B4AA';
const AMBER    = '#FFBC00';
const ATINT    = 'rgba(255,188,0,0.12)';
const ADARK    = '#A67800';
const TEAL     = '#004D40';
const TDARK    = '#002B25';
const TSOFT    = 'rgba(0,77,64,0.06)';
const SUCCESS  = '#059669';
const ERR      = '#DC2626';
const ONLINE   = '#22C55E';
const FD       = "'DM Serif Display', Georgia, serif";
const FB       = C.body;

// ── Tiny icon helper ──────────────────────────────────────────────────────
const Ic = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const SendIc    = p => <Ic {...p} d={<><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9z"/></>}/>;
const MailIc    = p => <Ic {...p} d={<><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></>}/>;
const UsersIc   = p => <Ic {...p} d={<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}/>;
const AwardIc   = p => <Ic {...p} d={<><circle cx="12" cy="8" r="6"/><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5"/></>}/>;
const TrendUpIc = p => <Ic {...p} d={<><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>}/>;
const SearchIc  = p => <Ic {...p} d={<><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>}/>;
const PlusIc    = p => <Ic {...p} d={<><path d="M12 5v14"/><path d="M5 12h14"/></>}/>;
const ChevIc    = p => <Ic {...p} d={<polyline points="6 9 12 15 18 9"/>}/>;

// ── Avatar helpers ────────────────────────────────────────────────────────
const AV_COLORS = ['#E04A3F','#E0A23F','#D88536','#3B82F6','#059669','#8B5CF6','#EC4899','#0EA5E9'];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + (name.charCodeAt(i) || 0)) % AV_COLORS.length;
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[1][0]).toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────
export default function MobileAnalytics({ stellen = [], save, markierteIds, onMarkierenToggle, onBearbeiten, onExternErstellen }) {

  // ── State ─────────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState('');
  const [filterTab,    setFilterTab]    = useState('alle');
  const [todoTab,      setTodoTab]      = useState('todo');
  const { todos, setTodos }             = useAnalyticsTodos({ fallback: INITIAL_TODOS });
  const [addingTodo,   setAddingTodo]   = useState(false);
  const [newTodoTitle, setNewTodoTitle] = useState('');
  const [sheetStelleId, setSheetStelleId] = useState(null);
  const [externOffen,  setExternOffen]  = useState(false);
  const [statusDropId, setStatusDropId] = useState(null);

  const sheetStelle = useMemo(
    () => stellen.find(s => s.id === sheetStelleId) || null,
    [stellen, sheetStelleId],
  );

  // ── KPIs ──────────────────────────────────────────────────────────────
  const gesamt    = stellen.filter(s => !istEntwurf(s)).length;
  const interview = useMemo(() => stellen.filter(s => s.bewerbungs_status === 'kennenlerngespraech').length, [stellen]);
  const zusage    = useMemo(() => stellen.filter(s => s.bewerbungs_status === 'zusage').length, [stellen]);
  const antwortQ  = gesamt > 0 ? Math.round(((interview + zusage) / gesamt) * 100) : 0;
  const trendGesamt = useMemo(() => trendVsPrev(stellen, () => true), [stellen]);

  // ── Chart ─────────────────────────────────────────────────────────────
  const buckets    = useMemo(() => computeWeeklyBuckets(stellen, 12), [stellen]);
  const chartTotal = useMemo(() => buckets.reduce((s, b) => s + b.sent + b.interview + b.zusage, 0), [buckets]);
  const recentTot  = useMemo(() => buckets.slice(6).reduce((s, b) => s + b.sent + b.interview + b.zusage, 0), [buckets]);
  const prevTot    = useMemo(() => buckets.slice(0, 6).reduce((s, b) => s + b.sent + b.interview + b.zusage, 0), [buckets]);
  const deltaPct   = prevTot === 0 ? 0 : Math.round((recentTot - prevTot) / prevTot * 100);
  const avgStr     = (chartTotal / 12).toFixed(1).replace('.', ',');
  const peakLabel  = useMemo(() => {
    const peak = buckets.reduce((p, b) => (b.sent + b.interview + b.zusage) > (p.sent + p.interview + p.zusage) ? b : p, buckets[0]);
    return peak ? peak.label : '—';
  }, [buckets]);

  // ── Filter chips ──────────────────────────────────────────────────────
  const CHIPS = useMemo(() => [
    { key: 'alle',                label: 'Alle',        count: gesamt },
    { key: 'abgeschickt',         label: 'Abgeschickt', count: stellen.filter(s => s.bewerbungs_status === 'abgeschickt').length },
    { key: 'kennenlerngespraech', label: 'Interview',   count: stellen.filter(s => s.bewerbungs_status === 'kennenlerngespraech').length },
    { key: 'zusage',              label: 'Zusage',      count: stellen.filter(s => s.bewerbungs_status === 'zusage').length },
    { key: 'absage',              label: 'Absage',      count: stellen.filter(s => s.bewerbungs_status === 'absage').length },
  ], [stellen]);

  const filtered = useMemo(() => stellen
    .filter(s => {
      if (filterTab !== 'alle' && s.bewerbungs_status !== filterTab) return false;
      if (search) {
        const q = search.toLowerCase();
        const name    = (s.empfaenger_unternehmen || s.name || '').toLowerCase();
        const contact = (s.empfaenger_ansprechperson || '').toLowerCase();
        if (!name.includes(q) && !contact.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.erstellt_am || 0) - new Date(a.erstellt_am || 0)),
  [stellen, filterTab, search]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const persistStelle = useCallback(async (stelleId, updates) => {
    if (save) {
      await save(stelleId, updates);
      return;
    }
    await updateStelle(stelleId, updates);
  }, [save]);

  const handleStatusChange = useCallback(async (stelle, newStatus) => {
    try {
      await persistStelle(stelle.id, { bewerbungs_status: newStatus });
    } catch (e) { console.error(e); }
    setStatusDropId(null);
  }, [persistStelle]);

  const addTodo = () => {
    if (!newTodoTitle.trim()) return;
    setTodos(ts => [...ts, { id: `t_${Date.now()}`, title: newTodoTitle.trim(), due: 'Offen', pri: 'normal', done: false }]);
    setNewTodoTitle('');
    setAddingTodo(false);
  };

  const KPI_CARDS = [
    { label: 'Bewerbungen gesamt', value: gesamt, unit: null, sub: `${trendGesamt >= 0 ? '+' : ''}${trendGesamt}% vs. Vorperiode`, accent: 'teal', Icon: SendIc, delta: trendGesamt > 0 ? 'up' : null },
    { label: 'Antwortquote', value: antwortQ,  unit: '%',  sub: 'Gesamt',         accent: 'amber', Icon: MailIc },
    { label: 'Interviews',   value: interview, unit: null, sub: `${interview} Gespräch${interview !== 1 ? 'e' : ''}`, accent: 'teal', Icon: UsersIc },
    { label: 'Zusagen',      value: zusage,    unit: null, sub: zusage > 0 ? 'Herzlichen Glückwunsch!' : '0% vs. Vorperiode', accent: 'teal', Icon: AwardIc },
  ];

  const openTodos = todos.filter(t => !t.done).length;

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'absolute', inset: 0, background: BG, color: INK, fontFamily: FB, WebkitFontSmoothing: 'antialiased', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
         onClick={() => statusDropId && setStatusDropId(null)}>
      <div style={{ paddingTop: 'calc(env(safe-area-inset-top,0px) + 68px)', paddingBottom: 'calc(env(safe-area-inset-bottom,0px) + 80px)' }}>

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <header style={{ padding: '0 22px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: TEAL }}>
              <span style={{ display: 'inline-block', width: 22, height: 2, borderRadius: 2, background: AMBER, marginRight: 8 }}/>
              DASHBOARD
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px 5px 9px', borderRadius: 999, background: SURFACE, border: '1px solid rgba(0,0,0,0.06)', fontSize: 11, fontWeight: 600, color: '#4A4A4A', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: ONLINE, boxShadow: `0 0 0 3px rgba(34,197,94,0.18)` }}/>
              Vorschau
            </span>
          </div>
          <h1 style={{ fontFamily: FD, fontWeight: 400, fontSize: 46, lineHeight: 1.0, letterSpacing: '-0.025em', color: INK, margin: '4px 0 0' }}>
            Karriere<span style={{ color: AMBER }}>.</span>
          </h1>
          <p style={{ margin: '10px 0 0', color: SOFT, fontSize: 14, lineHeight: 1.5 }}>
            Dein Bewerbungs-Cockpit — alles auf einen Blick.
          </p>
        </header>

        {/* ── KPI 2×2 ───────────────────────────────────────────────────── */}
        <section style={{ padding: '14px 18px 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {KPI_CARDS.map((k, i) => <KpiCard key={i} {...k} />)}
          </div>
        </section>

        {/* ── Chart ─────────────────────────────────────────────────────── */}
        <section style={{ padding: '20px 18px 0' }}>
          <SectionHead eyebrow="AKTIVITÄT · 12 WOCHEN" title="Bewerbungen pro Woche" />
          <div style={{ background: CREAM2, border: '1px solid rgba(0,0,0,0.05)', borderRadius: 18, padding: '14px 12px 12px', boxShadow: '0 1px 2px rgba(0,77,64,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0 6px 4px' }}>
              <div>
                <div style={{ fontFamily: FD, fontSize: 30, lineHeight: 1, color: INK, fontWeight: 400, letterSpacing: '-0.02em' }}>{chartTotal}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: MUTED, marginTop: 4, fontWeight: 500 }}>
                  <TrendUpIc size={12}/>
                  <span><b style={{ color: deltaPct >= 0 ? SUCCESS : ERR }}>{deltaPct >= 0 ? '+' : ''}{deltaPct}%</b> vs. Vorperiode</span>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 10.5, color: MUTED, lineHeight: 1.55, fontWeight: 500 }}>
                <div>Ø {avgStr} / Woche</div>
                <div>Peak <b style={{ color: INK }}>{peakLabel}</b></div>
              </div>
            </div>
            <WeeklyChart buckets={buckets} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '8px 6px 0' }}>
              <LegendItem color={TEAL}  label="Abgeschickt" />
              <LegendItem color={AMBER} label="Interview" />
              <LegendItem color={TDARK} label="Zusage" />
            </div>
          </div>
        </section>

        {/* ── To-Do / Kalender ──────────────────────────────────────────── */}
        <section style={{ padding: '20px 18px 0' }}>
          <SectionHead
            eyebrow={`${openTodos} OFFEN`}
            title="To-Do"
            right={
              <div style={{ display: 'inline-flex', padding: 3, background: TSOFT, borderRadius: 999 }}>
                {[['todo','To-Do'],['kalender','Kalender']].map(([key, lbl]) => (
                  <button key={key} onClick={() => setTodoTab(key)} style={{ padding: '5px 12px', borderRadius: 999, border: 'none', fontFamily: 'inherit', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, background: todoTab === key ? TEAL : 'transparent', color: todoTab === key ? '#fff' : SOFT, boxShadow: todoTab === key ? '0 1px 4px rgba(0,77,64,0.25)' : 'none', transition: 'background 0.2s' }}>
                    {lbl}
                  </button>
                ))}
              </div>
            }
          />
          {todoTab === 'todo' ? (
            <div style={{ background: SURFACE, border: '1px solid rgba(0,0,0,0.05)', borderRadius: 16, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {todos.map(t => (
                <div key={t.id}
                  onClick={() => setTodos(ts => ts.map(x => x.id === t.id ? { ...x, done: !x.done } : x))}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 12, cursor: 'pointer', opacity: t.done ? 0.55 : 1, background: t.pri === 'urgent' && !t.done ? 'rgba(255,188,0,0.10)' : 'rgba(255,188,0,0.06)', border: t.pri === 'urgent' && !t.done ? '1px solid rgba(255,188,0,0.20)' : '1px solid transparent' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 6, border: t.done ? 'none' : '1.5px solid rgba(0,77,64,0.30)', background: t.done ? TEAL : '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {t.done && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: INK, letterSpacing: '-0.005em', textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11, color: MUTED }}>
                      <span>{t.due}</span>
                      {t.pri === 'urgent' && !t.done && <span style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(220,38,38,0.10)', color: ERR, fontSize: 10, fontWeight: 700 }}>Dringend</span>}
                    </div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); setTodos(ts => ts.filter(x => x.id !== t.id)); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: FAINT, fontSize: 17, lineHeight: 1, padding: '0 2px', fontFamily: 'inherit' }}>×</button>
                </div>
              ))}
              {addingTodo ? (
                <div style={{ display: 'flex', gap: 8, padding: '8px 12px' }}>
                  <input autoFocus value={newTodoTitle} onChange={e => setNewTodoTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTodo(); if (e.key === 'Escape') setAddingTodo(false); }} placeholder="Neue Aufgabe…" style={{ flex: 1, border: '1px solid rgba(0,77,64,0.25)', borderRadius: 8, padding: '8px 12px', fontSize: 14, fontFamily: 'inherit', background: '#fff', outline: 'none', color: INK }}/>
                  <button onClick={addTodo} style={{ padding: '8px 14px', background: TEAL, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>OK</button>
                </div>
              ) : (
                <button onClick={() => setAddingTodo(true)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 12, borderRadius: 12, background: 'transparent', border: '1px dashed rgba(0,77,64,0.20)', color: TEAL, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <PlusIc size={14}/> Aufgabe hinzufügen
                </button>
              )}
            </div>
          ) : (
            <div className="analytics-view" style={{ padding: 0, flex: 'unset', width: 'auto', background: SURFACE, border: '1px solid rgba(0,0,0,0.05)', borderRadius: 16, overflow: 'hidden' }}>
              <AnalyticsCalendar stellen={stellen} />
            </div>
          )}
        </section>

        {/* ── Alle Stellen ──────────────────────────────────────────────── */}
        <section style={{ padding: '20px 18px 0' }}>
          <SectionHead
            eyebrow="BEWERBUNGEN"
            title="Alle Stellen"
            right={
              <button onClick={e => { e.stopPropagation(); setExternOffen(true); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid rgba(0,77,64,0.18)', color: TEAL, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <PlusIc size={13}/> Extern
              </button>
            }
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', height: 40, borderRadius: 999, background: SURFACE, border: '1px solid rgba(0,0,0,0.06)', color: MUTED, marginBottom: 10 }}>
            <SearchIc size={16}/>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Unternehmen suchen…" style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 14, fontFamily: 'inherit', color: INK }}/>
          </div>

          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 10, marginBottom: 4, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {CHIPS.map(ch => (
              <button key={ch.key} onClick={() => setFilterTab(ch.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px 6px 14px', borderRadius: 999, background: filterTab === ch.key ? TEAL : 'transparent', border: filterTab === ch.key ? `1px solid ${TEAL}` : '1px solid rgba(0,0,0,0.08)', color: filterTab === ch.key ? '#fff' : '#4A4A4A', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
                {ch.label}
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: filterTab === ch.key ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.06)', color: filterTab === ch.key ? '#fff' : MUTED, fontVariantNumeric: 'tabular-nums' }}>{ch.count}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '36px 20px', textAlign: 'center', color: MUTED, fontSize: 14, background: SURFACE, borderRadius: 16, border: '1px solid rgba(0,0,0,0.05)' }}>
                Keine Stellen gefunden
              </div>
            ) : filtered.map(s => (
              <StelleKarteMobile
                key={s.id}
                stelle={s}
                onOpen={() => setSheetStelleId(s.id)}
                onStatusChange={handleStatusChange}
                statusDropId={statusDropId}
                setStatusDropId={setStatusDropId}
              />
            ))}
          </div>
        </section>

      </div>

      {/* ── Detail Sheet ──────────────────────────────────────────────── */}
      <MobileBottomSheet isOpen={!!sheetStelle} onClose={() => setSheetStelleId(null)} initialSnap={2} variant="flush">
        {sheetStelle && (
          <StelleCard
            stelle={sheetStelle}
            expanded={true}
            mobileSheet
            onToggle={() => setSheetStelleId(null)}
            save={save}
            markierteIds={markierteIds}
            onMarkierenToggle={onMarkierenToggle}
            onBearbeiten={onBearbeiten}
          />
        )}
      </MobileBottomSheet>

      {/* ── Extern Modal ──────────────────────────────────────────────── */}
      {externOffen && (
        <ExternModal
          onSchliessen={() => setExternOffen(false)}
          onErstellen={async (daten) => {
            const res = await createStelle({ ...daten, quelle: 'extern' });
            onExternErstellen?.(res.data);
            setExternOffen(false);
          }}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function SectionHead({ eyebrow, title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>{eyebrow}</div>
        <h2 style={{ fontFamily: FD, fontWeight: 400, fontSize: 22, lineHeight: 1.2, color: INK, letterSpacing: '-0.01em', margin: 0 }}>{title}</h2>
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

function KpiCard({ label, value, unit, sub, accent, Icon, delta }) {
  const ac = accent === 'amber' ? AMBER : TEAL;
  const bg = accent === 'amber' ? ATINT  : TSOFT;
  return (
    <div style={{ background: SURFACE, border: '1px solid rgba(0,0,0,0.05)', borderRadius: 16, padding: '14px 14px 12px', boxShadow: '0 1px 2px rgba(0,77,64,0.04)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED }}>{label}</span>
        <span style={{ width: 24, height: 24, borderRadius: 8, background: bg, color: ac, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14}/>
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, margin: '8px 0 6px' }}>
        <span style={{ fontFamily: FD, fontWeight: 400, fontSize: 36, lineHeight: 1, color: INK, letterSpacing: '-0.02em' }}>{value}</span>
        {unit && <span style={{ fontFamily: FD, fontSize: 18, color: MUTED }}>{unit}</span>}
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: MUTED, fontWeight: 500 }}>
        {delta === 'up' && <TrendUpIc size={11}/>}
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{sub}</span>
      </div>
      <div style={{ position: 'absolute', left: 14, right: 14, bottom: 0, height: 2, borderRadius: 2, background: ac, opacity: 0.55 }}/>
    </div>
  );
}

function WeeklyChart({ buckets }) {
  const max  = Math.max(...buckets.map(b => b.sent + b.interview + b.zusage), 3);
  const W    = 322;
  const H    = 96;
  const slot = W / buckets.length;
  const bW   = Math.min(14, slot * 0.55);
  return (
    <div style={{ marginTop: 8, padding: '0 4px' }}>
      <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
        {[0, 0.5, 1].map((t, i) => (
          <line key={i} x1={0} x2={W} y1={H * t} y2={H * t} stroke="rgba(0,77,64,0.08)" strokeWidth="1"/>
        ))}
        {buckets.map((b, i) => {
          const cx = i * slot + slot / 2;
          const hS = (b.sent      / max) * (H - 8);
          const hI = (b.interview / max) * (H - 8);
          const hZ = (b.zusage    / max) * (H - 8);
          const bx = cx - bW / 2;
          const shortLabel = b.label.replace('KW', '');
          return (
            <g key={i}>
              {b.sent > 0      && <rect x={bx} y={H - hS}           width={bW} height={hS} rx={bW/2} fill={TEAL}  opacity="0.85"/>}
              {b.interview > 0 && <rect x={bx} y={H - hS - hI}      width={bW} height={hI}           fill={AMBER}/>}
              {b.zusage > 0    && <rect x={bx} y={H - hS - hI - hZ} width={bW} height={hZ} rx={bW/2} fill={TDARK}/>}
              <text x={cx} y={H + 13} textAnchor="middle"
                style={{ font: '600 9px Inter', fill: MUTED, letterSpacing: '0.04em' }}>
                {shortLabel}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, color: SOFT, fontWeight: 500 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'inline-block' }}/>
      {label}
    </div>
  );
}

function StelleKarteMobile({ stelle, onOpen, onStatusChange, statusDropId, setStatusDropId }) {
  const company  = stelle.empfaenger_unternehmen || stelle.name || 'Unbenannt';
  const contact  = stelle.empfaenger_ansprechperson || '—';
  const cfg      = STATUS_CONFIG[stelle.bewerbungs_status || ''] || STATUS_CONFIG[''];
  const isOpen   = statusDropId === stelle.id;
  const quelle   = plattformAusStelle(stelle);
  const abschick = stelle.abschick_datum ? formatDate(stelle.abschick_datum) : '—';
  const gehalt   = stelle.gehaltsvorstellung || '—';

  return (
    <div style={{ background: SURFACE, border: '1px solid rgba(0,0,0,0.05)', borderRadius: 16, padding: '14px 14px 12px', boxShadow: '0 1px 2px rgba(0,77,64,0.04)', cursor: 'pointer', position: 'relative' }}
         onClick={() => !isOpen && onOpen()}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: avatarColor(company), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.02em', flexShrink: 0 }}>
          {initials(company)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: INK, letterSpacing: '-0.005em', lineHeight: 1.3 }}>{company}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{contact}</div>
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); setStatusDropId(isOpen ? null : stelle.id); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px 4px 10px', borderRadius: 999, background: cfg.bg || BG, border: `1px solid ${cfg.border || 'rgba(0,0,0,0.06)'}`, color: cfg.color || MUTED, fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit', cursor: 'pointer' }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: cfg.color || FAINT }}/>
            {cfg.label}
            <ChevIc size={11}/>
          </button>
          {isOpen && (
            <div onClick={e => e.stopPropagation()}
              style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: '#fff', border: '1px solid rgba(0,0,0,0.10)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 168, padding: 4 }}>
              {STATUS_OPTIONEN.map(opt => (
                <button key={opt.value} onClick={e => { e.stopPropagation(); onStatusChange(stelle, opt.value); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', background: stelle.bewerbungs_status === opt.value ? TSOFT : 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: stelle.bewerbungs_status === opt.value ? 700 : 500, color: stelle.bewerbungs_status === opt.value ? TEAL : INK, fontFamily: 'inherit', borderRadius: 8 }}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Meta footer */}
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px dashed rgba(0,0,0,0.07)' }}>
        <MetaCol label="Quelle">
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: ADARK, background: ATINT, padding: '2px 7px', borderRadius: 999, alignSelf: 'flex-start', marginTop: 1 }}>{quelle}</span>
        </MetaCol>
        <div style={{ width: 1, alignSelf: 'stretch', margin: '2px 8px', background: 'rgba(0,0,0,0.06)' }}/>
        <MetaCol label="Abgeschickt"><span style={{ fontSize: 13, color: FAINT, fontWeight: 500 }}>{abschick}</span></MetaCol>
        <div style={{ width: 1, alignSelf: 'stretch', margin: '2px 8px', background: 'rgba(0,0,0,0.06)' }}/>
        <MetaCol label="Gehalt"><span style={{ fontSize: 13, color: FAINT, fontWeight: 500 }}>{gehalt}</span></MetaCol>
      </div>
    </div>
  );
}

function MetaCol({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: MUTED }}>{label}</span>
      {children}
    </div>
  );
}
